import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../contexts/ProposalContext";
import { useChat } from "../contexts/ChatContext";
import { useAuth } from "../contexts/AuthContext";

// Components
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import { apiClient } from "../services/apiClient";

const ProposalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentProposal,
    loading: proposalLoading,
    error: proposalError,
    fetchProposal,
    clearError,
  } = useProposal();

  const { createSession } = useChat();

  const [documents, setDocuments] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const loadProposalData = async () => {
      if (!id) return;

      setLoading(true);
      setError("");

      try {
        // Fetch proposal details
        await fetchProposal(parseInt(id));

        // Fetch documents for this proposal using apiClient
        try {
          const documentsResponse = await apiClient.getProposalDocuments(id);
          if (documentsResponse.success) {
            setDocuments(documentsResponse.data.documents || []);
          }
        } catch (docError) {
          console.warn("Failed to fetch documents:", docError);
          // Don't fail the whole page load for documents
        }

        // Fetch chat sessions for this proposal using apiClient
        try {
          const sessionsResponse = await apiClient.getChatSessions(
            parseInt(id)
          );
          if (sessionsResponse.success) {
            setChatSessions(sessionsResponse.data.sessions || []);
          }
        } catch (chatError) {
          console.warn("Failed to fetch chat sessions:", chatError);
          // Don't fail the whole page load for chat sessions
        }
      } catch (err) {
        console.error("Error loading proposal data:", err);
        setError("Failed to load proposal data");
      } finally {
        setLoading(false);
      }
    };

    loadProposalData();
  }, [id, fetchProposal]);

  const handleStartChat = async () => {
    try {
      const session = await createSession(parseInt(id));
      if (session) {
        navigate(`/workspace/${id}`);
      }
    } catch (error) {
      console.error("Error creating chat session:", error);
    }
  };

  const handleUploadDocuments = () => {
    navigate(`/documents/${id}`);
  };

  const handleEditProposal = () => {
    navigate(`/proposals/edit/${id}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (value) => {
    if (!value) return "Not specified";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      Active: "bg-emerald-100 text-emerald-800",
      Completed: "bg-blue-100 text-blue-800",
      Archived: "bg-slate-100 text-slate-800",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          statusColors[status] || statusColors.Active
        }`}
      >
        {status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      RFP: "bg-orange-100 text-orange-800",
      Past_Proposal: "bg-purple-100 text-purple-800",
    };

    const typeLabels = {
      RFP: "Active RFP",
      Past_Proposal: "Past Proposal",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          typeColors[type] || typeColors.RFP
        }`}
      >
        {typeLabels[type] || type}
      </span>
    );
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    const iconMap = {
      pdf: "file-pdf",
      doc: "file-word",
      docx: "file-word",
      xls: "file-excel",
      xlsx: "file-excel",
      ppt: "file-powerpoint",
      pptx: "file-powerpoint",
    };
    return iconMap[ext] || "file-alt";
  };

  if (loading || proposalLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading proposal details..." />
        </div>
      </Layout>
    );
  }

  if (error || proposalError) {
    return (
      <Layout title="Error">
        <ErrorMessage
          message={error || proposalError}
          onRetry={() => {
            setError("");
            clearError();
            window.location.reload();
          }}
        />
      </Layout>
    );
  }

  if (!currentProposal) {
    return (
      <Layout title="Not Found">
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            className="text-4xl text-slate-400 mb-4"
          />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Proposal Not Found
          </h2>
          <p className="text-slate-600 mb-6">
            The proposal you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
          >
            Return to Dashboard
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={currentProposal.proposal_name}>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between space-y-4 lg:space-y-0">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                {getTypeBadge(currentProposal.proposal_type)}
                {getStatusBadge(currentProposal.status)}
              </div>

              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                {currentProposal.proposal_name}
              </h1>

              {currentProposal.project_title && (
                <p className="text-lg text-slate-600 mb-2">
                  {currentProposal.project_title}
                </p>
              )}

              {currentProposal.customer_name && (
                <p className="text-primary-600 font-medium text-lg">
                  {currentProposal.customer_name}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleEditProposal}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="edit" />
                <span>Edit</span>
              </button>

              <button
                onClick={handleStartChat}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="robot" />
                <span>Enter Workspace</span>
              </button>

              <button
                onClick={handleUploadDocuments}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="upload" />
                <span>Manage Documents</span>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
            <div className="text-2xl font-bold text-slate-900 mb-1">
              {documents.length}
            </div>
            <div className="text-sm text-slate-600">Documents</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
            <div className="text-2xl font-bold text-slate-900 mb-1">
              {chatSessions.length}
            </div>
            <div className="text-sm text-slate-600">Chat Sessions</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
            <div className="text-2xl font-bold text-slate-900 mb-1">
              {formatCurrency(currentProposal.contract_value).replace("$", "")}
            </div>
            <div className="text-sm text-slate-600">Contract Value</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 text-center">
            <div className="text-2xl font-bold text-slate-900 mb-1">
              {currentProposal.due_date
                ? Math.ceil(
                    (new Date(currentProposal.due_date) - new Date()) /
                      (1000 * 60 * 60 * 24)
                  )
                : "--"}
            </div>
            <div className="text-sm text-slate-600">Days Until Due</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="border-b border-slate-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: "overview", label: "Overview", icon: "eye" },
                { id: "documents", label: "Documents", icon: "file-alt" },
                { id: "chat", label: "Chat History", icon: "comment" },
                { id: "timeline", label: "Timeline", icon: "clock" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Proposal Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Proposal Number
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {currentProposal.proposal_number || "Not assigned"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          RFP Number
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {currentProposal.rfp_number || "Not specified"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Created By
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {currentProposal.created_by_name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Created On
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {formatDate(currentProposal.created_at)}
                        </dd>
                      </div>
                    </div>
                  </div>

                  {/* Timeline Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Timeline & Value
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Due Date
                        </dt>
                        <dd
                          className={`text-sm ${
                            currentProposal.due_date &&
                            new Date(currentProposal.due_date) < new Date()
                              ? "text-red-600"
                              : "text-slate-900"
                          }`}
                        >
                          {formatDate(currentProposal.due_date)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Contract Value
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {formatCurrency(currentProposal.contract_value)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">
                          Customer Type
                        </dt>
                        <dd className="text-sm text-slate-900">
                          {currentProposal.customer_type || "Not specified"}
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {currentProposal.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Description
                    </h3>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="text-slate-700 whitespace-pre-wrap">
                        {currentProposal.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Keywords */}
                {currentProposal.project_keywords && (
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">
                      Keywords
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {currentProposal.project_keywords
                        .split(",")
                        .map((keyword, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                          >
                            {keyword.trim()}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === "documents" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Documents ({documents.length})
                  </h3>
                  <button
                    onClick={handleUploadDocuments}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon="plus" />
                    <span>Add Documents</span>
                  </button>
                </div>

                {documents.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon
                      icon="file-alt"
                      className="text-4xl text-slate-300 mb-4"
                    />
                    <h4 className="text-lg font-medium text-slate-900 mb-2">
                      No Documents
                    </h4>
                    <p className="text-slate-600 mb-6">
                      Upload RFP documents, past proposals, and supporting
                      materials.
                    </p>
                    <button
                      onClick={handleUploadDocuments}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
                    >
                      Upload Documents
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map((doc) => (
                      <div
                        key={doc.document_id}
                        className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-start space-x-3">
                          <FontAwesomeIcon
                            icon={getFileIcon(doc.file_name)}
                            className="text-2xl text-slate-400 mt-1"
                          />
                          <div className="flex-1">
                            <h4 className="font-medium text-slate-900 truncate">
                              {doc.original_file_name}
                            </h4>
                            <p className="text-sm text-slate-600">
                              {doc.type_name}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Uploaded {formatDate(doc.uploaded_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Chat History Tab */}
            {activeTab === "chat" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Chat Sessions ({chatSessions.length})
                  </h3>
                  <button
                    onClick={handleStartChat}
                    className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon="plus" />
                    <span>New Chat</span>
                  </button>
                </div>

                {chatSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon
                      icon="comment"
                      className="text-4xl text-slate-300 mb-4"
                    />
                    <h4 className="text-lg font-medium text-slate-900 mb-2">
                      No Chat Sessions
                    </h4>
                    <p className="text-slate-600 mb-6">
                      Start a conversation with AI about this proposal.
                    </p>
                    <button
                      onClick={handleStartChat}
                      className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200"
                    >
                      Start Chat
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatSessions.map((session) => (
                      <div
                        key={session.session_id}
                        onClick={() => navigate(`/chat/${session.session_id}`)}
                        className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors duration-200 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-slate-900">
                              {session.session_name}
                            </h4>
                            <p className="text-sm text-slate-600">
                              Last activity: {formatDate(session.last_activity)}
                            </p>
                          </div>
                          <FontAwesomeIcon
                            icon="chevron-right"
                            className="text-slate-400"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === "timeline" && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">
                  Activity Timeline
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2"></div>
                    <div>
                      <p className="font-medium text-slate-900">
                        Proposal Created
                      </p>
                      <p className="text-sm text-slate-600">
                        {formatDate(currentProposal.created_at)}
                      </p>
                    </div>
                  </div>

                  {documents.length > 0 && (
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-slate-900">
                          Documents Uploaded
                        </p>
                        <p className="text-sm text-slate-600">
                          {documents.length} documents added
                        </p>
                      </div>
                    </div>
                  )}

                  {chatSessions.length > 0 && (
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="font-medium text-slate-900">
                          AI Conversations Started
                        </p>
                        <p className="text-sm text-slate-600">
                          {chatSessions.length} chat sessions
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProposalDetail;
